import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, GoldButton } from "@/components/Primitives";
import {
  Compass, Loader2, Briefcase, Calculator, Landmark, ShieldCheck, Building2, BookOpen, ListChecks, RefreshCw,
} from "lucide-react";
import Disclaimer from "@/components/Disclaimer";

const ICONS = {
  planner: Briefcase, accountant: Calculator, bank_manager: Landmark,
  insurance_adviser: ShieldCheck, property_adviser: Building2, bookkeeper: BookOpen,
};
const COLORS = {
  planner: "#C6A45C", accountant: "#8FB996", bank_manager: "#9FB8C9",
  insurance_adviser: "#C99FA0", property_adviser: "#B79B86", bookkeeper: "#A8B79B",
};

function ScoreRing({ score }) {
  const r = 52, c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 70 ? "#8FB996" : pct >= 40 ? "#C6A45C" : "#C99FA0";
  return (
    <div className="relative w-36 h-36">
      <svg className="w-36 h-36 -rotate-90">
        <circle cx="72" cy="72" r={r} stroke="#EFE6D8" strokeWidth="10" fill="none" />
        <circle cx="72" cy="72" r={r} stroke={color} strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-light text-[#2C2A28]">{pct}</span>
        <span className="text-[10px] uppercase tracking-widest text-[#A99F90]">Health</span>
      </div>
    </div>
  );
}

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/insights");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not generate insights right now.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Insights" subtitle="A calm review from your virtual advisory team."
        action={
          <GoldButton data-testid="generate-insights-btn" onClick={generate} disabled={loading}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : data ? <RefreshCw size={16} /> : <Compass size={16} />}
            {loading ? "Consulting…" : data ? "Refresh" : "Generate Review"}
          </GoldButton>
        } />

      {err && <div data-testid="insights-error" className="text-sm text-[#b06b6b] bg-[#C99FA0]/15 border border-[#C99FA0]/30 rounded-xl px-4 py-3 mb-6">{err}</div>}

      {!data && !loading && (
        <div className="relative overflow-hidden rounded-2xl p-12 border border-[#C6A45C]/30 text-center" style={{ background: "linear-gradient(135deg, #FBF4E6, #FBF7F1)" }}>
          <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#C6A45C]/12 blur-3xl" />
          <Compass className="text-[#C6A45C] mx-auto mb-5" size={36} strokeWidth={1.2} />
          <h2 className="font-display text-3xl font-light mb-3 text-[#2C2A28]">Ready when you are</h2>
          <p className="text-[#8A7F70] max-w-lg mx-auto text-sm leading-relaxed">
            Your advisory team reviews your income, spending, savings and investments to offer warm, practical
            guidance — tailored to your numbers and your plan.
          </p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 text-[#8A7F70]">
          <Loader2 size={36} className="animate-spin text-[#C6A45C] mb-4" />
          <div className="font-display text-xl text-[#2C2A28]">Your team is reviewing your finances…</div>
        </div>
      )}

      {data && !loading && (
        <div className="space-y-5">
          <Card className="fade-up relative overflow-hidden border-[#C6A45C]/30">
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#C6A45C]/12 blur-3xl" />
            <div className="flex flex-col md:flex-row items-center gap-8">
              <ScoreRing score={data.health_score} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] gold-text mb-2">Panel Verdict</div>
                <h2 data-testid="insights-headline" className="font-display text-2xl md:text-3xl font-light leading-snug text-[#2C2A28]">{data.headline}</h2>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {(data.advisers || []).map((adv, idx) => {
              const Icon = ICONS[adv.key] || Briefcase;
              const color = COLORS[adv.key] || "#C6A45C";
              return (
                <Card key={adv.key} className="fade-up" style={{ animationDelay: `${idx * 80}ms` }} data-testid={`panel-${adv.key}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
                      <Icon size={18} style={{ color }} />
                    </div>
                    <h3 className="font-display text-lg text-[#2C2A28]">{adv.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {(data[adv.key] || []).map((t, i) => (
                      <li key={i} className="text-sm text-[#6b6256] leading-relaxed flex gap-2">
                        <span style={{ color }} className="mt-1.5">•</span>{t}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <Card className="fade-up border-[#C6A45C]/25">
            <div className="flex items-center gap-3 mb-5">
              <ListChecks size={20} className="text-[#C6A45C]" />
              <h3 className="font-display text-xl text-[#2C2A28]">Priority Actions</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(data.actions || []).map((a, i) => (
                <div key={i} className="bg-white/60 border border-[#EAE0D2] rounded-xl p-4">
                  <div className="font-display text-2xl gold-text mb-2">{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-sm text-[#6b6256] leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <Disclaimer className="mt-8" />
    </div>
  );
}
